"use client";

// Shared chat-row actions for the COMMAND page panel and the Talk to Command
// drawer: the ⋯ menu (pin, rename, export, move to project, delete), the
// rename dialog, and client-side export of a session (the prototype has no
// API — markdown is built from the store's messages; PDF goes through a
// print window where the browser's "Save as PDF" does the work).

import { useEffect, useState } from "react";
import {
  Copy,
  Download,
  FileText,
  Folder,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { COMMAND_V2_DIALOG_CONTENT_CLASS } from "@/components/command-team-v2/utils/dialog-styles";

import type { ProtoChatSession, ProtoProject } from "../prototype/types";

// ---------------------------------------------------------------------------
// Export

export function sessionToMarkdown(session: ProtoChatSession): string {
  const lines: string[] = [`# ${session.title}`, ""];
  for (const message of session.messages) {
    if (message.pending) continue;
    lines.push(`**${message.role === "user" ? "You" : "Command"}**`, "");
    lines.push(message.content, "");
    if (message.attachedFiles?.length) {
      lines.push(
        `_Attached: ${message.attachedFiles.map((file) => file.name).join(", ")}_`,
        ""
      );
    }
  }
  return lines.join("\n");
}

function slugify(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "command-chat"
  );
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export type ChatExportFormat = "markdown" | "pdf" | "clipboard";

export async function exportSession(
  session: ProtoChatSession,
  format: ChatExportFormat
): Promise<void> {
  const markdown = sessionToMarkdown(session);

  if (format === "markdown") {
    downloadBlob(
      `${slugify(session.title)}.md`,
      new Blob([markdown], { type: "text/markdown;charset=utf-8" })
    );
    toast.success("Markdown downloaded");
    return;
  }

  if (format === "clipboard") {
    try {
      await navigator.clipboard.writeText(markdown);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access the clipboard");
    }
    return;
  }

  // PDF — print window; the browser's destination picker offers "Save as PDF"
  const win = window.open("", "_blank", "width=800,height=1000");
  if (!win) {
    toast.error("Allow pop-ups to export a PDF");
    return;
  }
  const body = session.messages
    .filter((message) => !message.pending)
    .map(
      (message) => `
        <h2>${message.role === "user" ? "You" : "Command"}</h2>
        <p>${escapeHtml(message.content)}</p>
        ${
          message.attachedFiles?.length
            ? `<p class="files">Attached: ${escapeHtml(
                message.attachedFiles.map((file) => file.name).join(", ")
              )}</p>`
            : ""
        }`
    )
    .join("");
  win.document.write(`<!doctype html><html><head><title>${escapeHtml(
    session.title
  )}</title><style>
    body{font:14px/1.6 system-ui,sans-serif;color:#111;max-width:720px;margin:40px auto;padding:0 24px}
    h1{font-size:20px;margin:0 0 16px}
    h2{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#7c3aed;margin:20px 0 4px}
    p{white-space:pre-wrap;margin:0 0 6px}
    .files{font-style:italic;color:#666;font-size:12px}
  </style></head><body><h1>${escapeHtml(session.title)}</h1>${body}</body></html>`);
  win.document.close();
  win.focus();
  win.print();
}

// ---------------------------------------------------------------------------
// ⋯ menu — one menu for chat rows everywhere

export function ChatRowMenu({
  session,
  projects,
  onTogglePin,
  onRename,
  onMove,
  onDelete,
}: {
  session: ProtoChatSession;
  projects: ProtoProject[];
  onTogglePin: () => void;
  onRename: () => void;
  onMove: (projectId: string | null) => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label={`Options for “${session.title}”`}
          className="hidden size-6 shrink-0 p-0 group-hover:flex data-[state=open]:flex"
        >
          <MoreHorizontal className="size-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuItem onClick={onTogglePin}>
          {session.pinned ? (
            <PinOff className="mr-2 size-3.5" />
          ) : (
            <Pin className="mr-2 size-3.5" />
          )}
          {session.pinned ? "Unpin" : "Pin to top"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onRename}>
          <Pencil className="mr-2 size-3.5" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Download className="mr-2 size-3.5" />
            Export
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => void exportSession(session, "markdown")}>
              <FileText className="mr-2 size-3.5" />
              Markdown (.md)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void exportSession(session, "pdf")}>
              <FileText className="mr-2 size-3.5" />
              PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void exportSession(session, "clipboard")}>
              <Copy className="mr-2 size-3.5" />
              Copy to Clipboard
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Folder className="mr-2 size-3.5" />
            Move to project…
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {projects.map((project) => (
              <DropdownMenuItem
                key={project.id}
                disabled={session.projectId === project.id}
                onClick={() => onMove(project.id)}
              >
                <Folder className="mr-2 size-3.5" />
                {project.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!session.projectId}
              onClick={() => onMove(null)}
            >
              Other chats
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 size-3.5" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Rename dialog

export function RenameChatDialog({
  session,
  onClose,
  onRename,
}: {
  session: ProtoChatSession | null;
  onClose: () => void;
  onRename: (sessionId: string, title: string) => void;
}) {
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (session) setTitle(session.title);
  }, [session]);

  const submit = () => {
    const trimmed = title.trim();
    if (!session || !trimmed) return;
    onRename(session.id, trimmed);
    onClose();
  };

  return (
    <Dialog open={!!session} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(COMMAND_V2_DIALOG_CONTENT_CLASS, "max-w-sm")}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-[9px] bg-violet-100 p-2 dark:bg-violet-900/30">
              <Pencil className="size-4 text-primary" />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-left text-lg font-semibold tracking-tight">
                Rename chat
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                The conversation keeps its messages — only the title changes.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="mt-6">
          <Label className="font-semibold text-foreground">Chat title</Label>
          <Input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
            }}
            className="mt-2"
          />
        </div>

        <div className="mt-6 flex shrink-0 items-center justify-end gap-4 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-foreground transition-opacity hover:opacity-70"
          >
            Cancel
          </button>
          <Button
            type="button"
            onClick={submit}
            disabled={!title.trim()}
            className="h-10 rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
